import React, { useState, useEffect } from 'react';
import { 
  Search, Users, UserPlus, UserMinus, ChefHat, 
  Loader2, TrendingUp, Sparkles 
} from 'lucide-react';
import { followService } from '../../services/followService';

export const DiscoverChefs = ({ currentUserId, onSelectChef }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [chefs, setChefs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [followingStates, setFollowingStates] = useState({});
  const [loadingFollows, setLoadingFollows] = useState({});

  useEffect(() => {
    fetchChefs();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchChefs(searchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const fetchChefs = async (query = '') => {
    setIsLoading(true);
    try {
      const data = await followService.searchChefs(query, 20);
      setChefs(data);
      
      // Check follow status for each chef if user is logged in
      if (currentUserId) {
        const states = {};
        for (const chef of data) {
          if (chef.id !== currentUserId) {
            try {
              states[chef.id] = await followService.checkFollowStatus(currentUserId, chef.id);
            } catch {
              states[chef.id] = false;
            }
          }
        }
        setFollowingStates(states);
      }
    } catch (e) {
      console.error('Error fetching chefs:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFollow = async (chefId, e) => {
    e.stopPropagation();
    if (!currentUserId || currentUserId === chefId) return;
    
    setLoadingFollows(prev => ({ ...prev, [chefId]: true }));
    
    try {
      const isCurrentlyFollowing = followingStates[chefId];
      
      if (isCurrentlyFollowing) {
        await followService.unfollowUser(currentUserId, chefId);
        setFollowingStates(prev => ({ ...prev, [chefId]: false }));
        setChefs(prev => prev.map(chef => 
          chef.id === chefId 
            ? { ...chef, followers_count: Math.max(0, (chef.followers_count || 0) - 1) }
            : chef
        ));
      } else {
        await followService.followUser(currentUserId, chefId);
        setFollowingStates(prev => ({ ...prev, [chefId]: true }));
        setChefs(prev => prev.map(chef => 
          chef.id === chefId 
            ? { ...chef, followers_count: (chef.followers_count || 0) + 1 }
            : chef
        ));
      }
    } catch (e) {
      console.error('Error toggling follow:', e);
      alert(e.message || 'Error al procesar la acción');
    } finally {
      setLoadingFollows(prev => ({ ...prev, [chefId]: false }));
    }
  };

  return (
    <div data-testid="discover-chefs" className="animate-in fade-in duration-500">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-primary/10 text-primary rounded-2xl">
            <Users size={28} />
          </div>
          <div>
            <h2 className="text-4xl font-black heading-serif dark:text-white">Descubrir Chefs</h2>
            <p className="text-stone-500 dark:text-stone-400 font-medium">Encuentra y sigue a los mejores cocineros</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-xl">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-stone-300" size={20} />
          <input 
            data-testid="chef-search-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar chefs por nombre..."
            className="w-full pl-16 pr-6 py-5 rounded-[24px] bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 focus:ring-4 focus:ring-primary/5 outline-none font-bold dark:text-white placeholder:text-stone-300 transition-all text-base shadow-sm"
          />
        </div>
      </div>

      {/* Popular Chefs Section */}
      {!searchQuery && (
        <div className="mb-8 flex items-center gap-3">
          <TrendingUp size={18} className="text-primary" />
          <h3 className="text-[10px] font-black uppercase tracking-widest text-stone-400">
            Chefs populares
          </h3>
        </div>
      )}

      {searchQuery && (
        <div className="mb-8 flex items-center gap-3">
          <Sparkles size={18} className="text-primary" />
          <h3 className="text-[10px] font-black uppercase tracking-widest text-stone-400">
            Resultados para "{searchQuery}"
          </h3>
        </div>
      )}

      {/* Chefs Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-56 bg-stone-100 dark:bg-stone-800 rounded-[32px] animate-pulse" />
          ))}
        </div>
      ) : chefs.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {chefs.filter(chef => chef.id !== currentUserId).map(chef => (
            <div
              key={chef.id}
              data-testid={`chef-card-${chef.id}`}
              onClick={() => onSelectChef && onSelectChef(chef.id)}
              className="bg-white dark:bg-stone-900 rounded-[32px] border border-stone-100 dark:border-stone-800 p-6 hover:shadow-xl hover:border-primary/30 transition-all cursor-pointer group"
            >
              <div className="flex flex-col items-center text-center">
                <img 
                  src={chef.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${chef.id}`}
                  className="w-20 h-20 rounded-[24px] object-cover border-4 border-stone-50 dark:border-stone-800 shadow-lg mb-4 group-hover:scale-105 transition-transform"
                  alt={chef.display_name}
                />
                
                <h3 className="font-black text-lg text-stone-900 dark:text-white mb-1 truncate w-full">
                  {chef.display_name}
                </h3>
                <p className="text-sm text-stone-400 mb-4 truncate w-full">@{chef.username}</p>
                
                {chef.bio && (
                  <p className="text-xs text-stone-500 dark:text-stone-400 mb-4 line-clamp-2 italic">
                    "{chef.bio}"
                  </p>
                )}
                
                <div className="flex items-center justify-center gap-6 mb-4">
                  <div className="text-center">
                    <p className="font-black text-stone-900 dark:text-white">{chef.recipes_count || 0}</p>
                    <p className="text-[9px] font-bold uppercase text-stone-400">Recetas</p>
                  </div>
                  <div className="text-center">
                    <p className="font-black text-stone-900 dark:text-white">{chef.followers_count || 0}</p>
                    <p className="text-[9px] font-bold uppercase text-stone-400">Seguidores</p>
                  </div>
                </div>
                
                {currentUserId && currentUserId !== chef.id && (
                  <button
                    onClick={(e) => handleFollow(chef.id, e)}
                    disabled={loadingFollows[chef.id]}
                    className={`w-full py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2 ${
                      followingStates[chef.id]
                        ? 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/20'
                        : 'bg-primary text-white shadow-lg shadow-primary/20 hover:bg-primary/90'
                    }`}
                  >
                    {loadingFollows[chef.id] ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : followingStates[chef.id] ? (
                      <><UserMinus size={14} /> Siguiendo</>
                    ) : (
                      <><UserPlus size={14} /> Seguir</>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-stone-50 dark:bg-stone-900/50 rounded-[40px] border-2 border-dashed border-stone-100 dark:border-stone-800">
          <ChefHat size={64} className="mx-auto mb-6 text-stone-200" />
          <h3 className="text-2xl font-black heading-serif text-stone-900 dark:text-white mb-2">
            No encontramos chefs
          </h3>
          <p className="text-stone-500 font-medium">
            {searchQuery ? 'Prueba con otro término de búsqueda' : 'Aún no hay chefs registrados'}
          </p>
        </div>
      )}
    </div>
  );
};

export default DiscoverChefs;
